import FilterPanel from '@components/FilterPanel/FilterPanel';
import React, { FunctionComponent, useEffect, useMemo, useState, useCallback } from 'react';
import { Form, Button, Radio } from 'antd';
import {
  SortDescendingOutlined,
  SortAscendingOutlined,
  ClearOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useStore } from 'effector-react';
import useTranslate from '@hooks/useTranslate';
import experiments, { getList, setFilters } from '@stores/experiments';
import componentContent from './ExperimentsFilters.content.json';
import css from './ExperimentsFilters.module.less';

const ExperimentsFilters: FunctionComponent = () => {
  const { filters, preloaders } = useStore(experiments);
  const [form] = Form.useForm();
  const content = useTranslate(componentContent);

  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const initialValues = useMemo(() => {
    return {
      active: filters.active ?? undefined,
      forAll: filters.forAll ?? undefined,
    };
  }, [filters]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  const toggleSortOrder = useCallback(() => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
  }, []);

  const onFinish = (values: any) => {
    const newFilters = {
      ...values,
      active: values.active,
      forAll: values.forAll,
    };
    setFilters(newFilters);
    getList({ filters: newFilters, sort: `updated,${sortOrder}` });
  };

  const clearFilters = () => {
    form.resetFields();
    setFilters({});
    getList({ filters: {}, sort: `updated,${sortOrder}` });
  };

  return (
    <Form
      layout='vertical'
      form={form}
      initialValues={initialValues}
      onFinish={onFinish}
      className={css.form}
    >
      <FilterPanel
        actions={
          <div className={css.formFooter}>
            <Button
              htmlType='submit'
              icon={<SearchOutlined />}
              type='primary'
              loading={!!preloaders.getList}
            >
              {content.search}
            </Button>
            <Button
              htmlType='button'
              icon={<ClearOutlined />}
              onClick={clearFilters}
              loading={!!preloaders.getList}
            >
              {content.clear}
            </Button>
          </div>
        }
      >
        <Form.Item
          label={content.activeLabel}
          name='active'
        >
          <Radio.Group buttonStyle='solid'>
            <Radio.Button value={undefined}>{content.all}</Radio.Button>
            <Radio.Button value={true}>{content.enabled}</Radio.Button>
            <Radio.Button value={false}>{content.disabled}</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label={content.forAllLabel}
          name='forAll'
        >
          <Radio.Group buttonStyle='solid'>
            <Radio.Button value={undefined}>{content.all}</Radio.Button>
            <Radio.Button value={true}>{content.forAllEnabled}</Radio.Button>
            <Radio.Button value={false}>{content.forAllDisabled}</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item>
          <Button
            onClick={toggleSortOrder}
            type='link'
            style={{ margin: 0, padding: 0 }}
            className={css.sortButton}
          >
            {content.sortByDate}{' '}
            {sortOrder === 'desc' ? <SortDescendingOutlined /> : <SortAscendingOutlined />}
          </Button>
        </Form.Item>
      </FilterPanel>
    </Form>
  );
};

export default ExperimentsFilters;
